import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@/utils/errorMessage';
import { ClubService, type AssignClubToUserResult } from '../club.service';

export const useCreateClub = (onSuccess?: (club: Clubs.Club) => void) => {
	const queryClient = useQueryClient();

	const createClubMutation = useMutation({
		mutationFn: (body: Clubs.Create) => ClubService.create(body),
		onSuccess: (club) => {
			// Invalida a lista de clubes para forçar o componente a atualizar os dados
			queryClient.invalidateQueries({ queryKey: ['clubs'] });

			toast.success('Clube criado com sucesso!');
			if (onSuccess) onSuccess(club);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro na criação do clube'));
		},
	});

	return createClubMutation;
};

export const usePatchClub = (onSuccess?: (club: Clubs.Club) => void) => {
	const queryClient = useQueryClient();

	const patchClubMutation = useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string;
			body: Partial<Clubs.Create>;
		}) => ClubService.patch(id, body),
		onSuccess: (club) => {
			queryClient.invalidateQueries({ queryKey: ['clubs'] });
			toast.success('Clube atualizado com sucesso!');
			if (onSuccess) onSuccess(club);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro na atualização do clube'));
		},
	});

	return patchClubMutation;
};

export const useRemoveClub = (onSuccess?: (club: Clubs.Club) => void) => {
	const queryClient = useQueryClient();

	const removeClubMutation = useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string;
			body: Partial<Clubs.Create>;
		}) => ClubService.remove(id, body),
		onSuccess: (club) => {
			queryClient.invalidateQueries({ queryKey: ['clubs'] });
			toast.success('Clube removido com sucesso!');
			if (onSuccess) onSuccess(club);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro na remoção do clube'));
		},
	});

	return removeClubMutation;
};

export const useCreateClubBenefit = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: Clubs.CreateBenefit) =>
			ClubService.createBenefit(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['clubs'] });

			toast.success('Benefício vinculado com sucesso!');

			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao vincular benefício'));
		},
	});
};

export const useRemoveClubBenefit = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => ClubService.removeBenefit(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['clubs'] });
			toast.success('Vínculo removido com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao remover vínculo'));
		},
	});
};

export const useUpdateClubBenefit = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: Clubs.UpdateBenefit }) =>
			ClubService.updateBenefit(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['clubs'] });
			toast.success('Vínculo atualizado com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao atualizar vínculo'));
		},
	});
};

export type AssignClubRedirect = 'sameTab' | 'newTab' | 'none';

export const useAssignClub = (
	onSuccess?: (result: AssignClubToUserResult) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			clubId,
			userId,
			returnUrl = '/admin/clubes',
			redirect: _redirect = 'sameTab',
		}: {
			clubId: string;
			userId: string;
			returnUrl?: string;
			redirect?: AssignClubRedirect;
		}) => ClubService.assign(clubId, userId, { return_url: returnUrl }),
		onSuccess: (result, variables) => {
			queryClient.invalidateQueries({ queryKey: ['user'] });
			queryClient.invalidateQueries({ queryKey: ['users'] });
			if ('url' in result && result.url) {
				const mode = variables.redirect ?? 'sameTab';
				if (mode === 'sameTab') {
					toast.success('Redirecionando para o pagamento…');
					window.location.href = result.url;
				} else if (mode === 'newTab') {
					toast.success('Checkout gerado. Abrindo em nova aba…');
					window.open(result.url, '_blank', 'noopener,noreferrer');
				}
				// mode === 'none': tela trata toasts / cópia / abrir aba
			} else if ('activated' in result && result.activated) {
				toast.success(
					result.message ?? 'Clube ativado para o usuário (B2B2C).',
				);
			} else {
				toast.success('Operação concluída.');
			}
			onSuccess?.(result);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao vincular assinatura'));
		},
	});
};

export const useCancelUserClubSubscription = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (subscriptionId: string) =>
			ClubService.cancelUserSubscription(subscriptionId),
		onSuccess: async (data) => {
			await queryClient.invalidateQueries({ queryKey: ['user'] });
			await queryClient.invalidateQueries({ queryKey: ['users'] });
			toast.success(
				typeof data?.message === 'string'
					? data.message
					: 'Cancelamento processado.',
			);
			onSuccess?.();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao cancelar assinatura'));
		},
	});
};

export const useReconcileUserClubSubscription = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			clubId,
			userId,
			stripe_subscription_id,
		}: {
			clubId: string;
			userId: string;
			stripe_subscription_id?: string;
		}) =>
			ClubService.reconcileSubscription(clubId, userId, {
				...(stripe_subscription_id?.trim()
					? { stripe_subscription_id: stripe_subscription_id.trim() }
					: {}),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['user'] });
			queryClient.invalidateQueries({ queryKey: ['users'] });
			toast.success('Assinatura reconciliada com o Stripe.');
			onSuccess?.();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao reconciliar assinatura'),
			);
		},
	});
};

export const useJoinClub = (onSuccess?: (checkoutUrl: string) => void) => {
	return useMutation({
		mutationFn: ({
			clubId,
			successUrl = 'https://plumaa.com/clubs',
			cancelUrl = 'https://plumaa.com/clubs',
		}: {
			clubId: string;
			successUrl?: string;
			cancelUrl?: string;
		}) =>
			ClubService.join(clubId, {
				success_url: successUrl,
				cancel_url: cancelUrl,
			}),
		onSuccess: (checkoutUrl) => {
			toast.success('Checkout iniciado com sucesso!');
			if (onSuccess) onSuccess(checkoutUrl);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao iniciar checkout do clube'),
			);
		},
	});
};

export const usePatchUserClubBenefit = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			userId,
			body,
		}: {
			userId: string;
			body: Clubs.UpdateUserBenefitUsageBody;
		}) => ClubService.patchUserBenefit(userId, body),
		onSuccess: async (_data, variables) => {
			await queryClient.invalidateQueries({
				queryKey: ['clubs', 'user-benefits-balance', variables.userId],
			});
			await queryClient.invalidateQueries({
				queryKey: ['clubs', 'user-history', variables.userId],
			});
			await queryClient.invalidateQueries({ queryKey: ['user'] });
			toast.success('Uso do benefício atualizado.');
			onSuccess?.();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao atualizar uso do benefício'),
			);
		},
	});
};
